// src/schemas/font-catalog.ts
import { Type } from "@sinclair/typebox";
var SAFE_FONTS = [
  "Arial",
  "Calibri",
  "Cambria",
  "Consolas",
  "Courier New",
  "Georgia",
  "Segoe UI",
  "Tahoma",
  "Times New Roman",
  "Trebuchet MS",
  "Verdana",
  "Helvetica",
  "Helvetica Neue",
  "Menlo",
  "Monaco"
];
function isSafeFont(name) {
  const lower = name.toLowerCase();
  return SAFE_FONTS.some((f) => f.toLowerCase() === lower);
}
var FontFamilyNameSchema = Type.String({
  description: "Font family name. Prefer a SAFE_FONTS entry (Arial, Calibri, Cambria, Consolas, Courier New, Georgia, Segoe UI, Tahoma, Times New Roman, Trebuchet MS, Verdana, Helvetica, Helvetica Neue, Menlo, Monaco) for zero-setup rendering. For any other font, register it in props.fontRegistry[] and reference it here by family. Unregistered non-safe names render with a host fallback.",
  examples: ["Arial", "Calibri", "Georgia", "Inter", "Roboto"]
});
var FontWeightSchema = Type.Number({
  minimum: 100,
  maximum: 900,
  description: "OpenType weight (100 thin ... 900 black). Default 400."
});
var FontItalicSchema = Type.Boolean({
  description: "Whether this source is italic. Default false."
});
var SafeFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("safe"),
    family: Type.String({
      description: "A SAFE_FONTS name \u2014 installed with Office."
    })
  },
  {
    additionalProperties: false,
    description: "Office-installed font \u2014 no embedding"
  }
);
var GoogleFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("google"),
    family: Type.String({
      description: 'Exact Google Fonts family name (e.g. "Inter").'
    }),
    weights: Type.Optional(
      Type.Array(FontWeightSchema, {
        description: "Weights to fetch. Default [400, 700]."
      })
    ),
    italics: Type.Optional(
      Type.Boolean({ description: "Include italic variants. Default false." })
    )
  },
  {
    additionalProperties: false,
    description: "Google Fonts \u2014 auto-fetched and embedded"
  }
);
var FileFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("file"),
    path: Type.String({
      description: "Path to a .ttf/.otf file. Relative paths are resolved against the JSON document file."
    }),
    weight: Type.Optional(FontWeightSchema),
    italic: Type.Optional(FontItalicSchema)
  },
  { additionalProperties: false, description: "Local font file to embed" }
);
var UrlFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("url"),
    url: Type.String({
      description: "HTTPS URL of a TTF or OTF file."
    }),
    weight: Type.Optional(FontWeightSchema),
    italic: Type.Optional(FontItalicSchema)
  },
  {
    additionalProperties: false,
    description: "Direct TTF/OTF URL (non-Google CDN)"
  }
);
var VariableFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("variable"),
    url: Type.String({
      description: "HTTPS URL of a variable TTF (`fvar` axis table required)."
    }),
    weight: FontWeightSchema,
    italic: Type.Optional(FontItalicSchema),
    axes: Type.Optional(
      Type.Record(Type.String(), Type.Number(), {
        description: "Additional axis pin values (e.g. `{ ital: 1, opsz: 14 }`) merged on top of the derived `wght` pin. Uncommon \u2014 the `weight` field is usually enough."
      })
    )
  },
  {
    additionalProperties: false,
    description: "Variable-font instancer. The fetcher pins `wght` to `weight` (plus any `axes` overrides) and emits a clean static TTF, bypassing upstream distributions that collapse multiple static weights onto the same glyph geometry."
  }
);
var DataFontSourceSchema = Type.Object(
  {
    kind: Type.Literal("data"),
    data: Type.String({
      description: "Base64-encoded TTF/OTF or data: URL (data:font/ttf;base64,...). Makes the JSON self-contained at the cost of size."
    }),
    weight: Type.Optional(FontWeightSchema),
    italic: Type.Optional(FontItalicSchema)
  },
  { additionalProperties: false, description: "Inline base64 font \u2014 portable" }
);
var FontSourceSchema = Type.Union(
  [
    SafeFontSourceSchema,
    GoogleFontSourceSchema,
    FileFontSourceSchema,
    DataFontSourceSchema,
    UrlFontSourceSchema,
    VariableFontSourceSchema
  ],
  {
    description: 'A single font variant source. Use kind:"safe" for installed fonts, kind:"google" for Google Fonts, kind:"file" for local files, kind:"data" for base64, kind:"url" for direct HTTPS TTF/OTF URLs, kind:"variable" to instance a variable TTF at a specific weight.'
  }
);
var FontCategorySchema = Type.Union(
  [
    Type.Literal("sans"),
    Type.Literal("serif"),
    Type.Literal("mono"),
    Type.Literal("display"),
    Type.Literal("handwriting")
  ],
  { description: "Broad category used for fallback selection" }
);
var FontRegistryEntrySchema = Type.Object(
  {
    id: Type.String({
      description: 'Registry key. By convention, match the display family name ("Inter", "Roboto Slab").'
    }),
    family: Type.String({
      description: "Display family used in font.family / fontFace / theme.fonts.*. Usually identical to id."
    }),
    category: Type.Optional(FontCategorySchema),
    sources: Type.Array(FontSourceSchema, {
      minItems: 1,
      description: "One or more weight/style variants. List at least a regular (weight 400, italic false)."
    })
  },
  {
    additionalProperties: false,
    description: "A font registered for this document. Referenced by family from font.family, fontFace, and theme.fonts.*."
  }
);
var FontRegistrySchema = Type.Array(FontRegistryEntrySchema, {
  description: "Document-scoped font registry. Every non-safe font used in this document must be registered here."
});

export {
  SAFE_FONTS,
  isSafeFont,
  FontFamilyNameSchema,
  FontSourceSchema,
  FontRegistryEntrySchema,
  FontRegistrySchema
};
//# sourceMappingURL=chunk-6KUQYVPT.js.map